import qs from 'qs'

import HttpClient from 'services/HttpClient'
import {
  IAutoTranslateSingleProductPayload,
  IBatchTranslateRequest,
  ICopyProductsPayload,
  ICopySingleProductPayload,
  IGenerateMultipleProductPackagePayload,
  IGenerateSingleProductPackagePayload,
} from 'types/products'
import { IQuery } from 'types/slices'

export class ProductsRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  // Products
  getOwnershipLabels({ query }: { query: IQuery }) {
    return this.api.get(`/products/ownership-labels?${qs.stringify(query)}`)
  }

  getProducts({ query }: { query: IQuery }) {
    const initialQuery = {
      order: {
        by: 'createdAt',
        dir: 'desc'
      },
      limit: { take: 8, page: 1 },
      filter: { country: 'PL' }
    }
    const params = { ...initialQuery, ...query }

    return this.api.get(`/products?${qs.stringify(params)}`)
  }

  getProductsAll(params?: { productCategoryId?: string }) {
    const query = params?.productCategoryId
      ? { filter: { productCategory: { id: params.productCategoryId } } }
      : {}

    return this.api.get(`/products/all?${qs.stringify(query)}`)
  }

  getProduct(id: string) {
    return this.api.get(`/products/${id}`, { skipAuth: true })
  }

  createProduct(payload: FormData) {
    return this.api.post(`/products/form`, payload)
  }

  editProduct(id: string, formData: FormData) {
    return this.api.patch(`/products/${id}/form`, formData)
  }

  deleteProduct(id: string) {
    return this.api.delete(`/products/${id}`)
  }

  generateSingleProductPackage(payload: IGenerateSingleProductPackagePayload) {
    return this.api.post(`/packages`, payload)
  }

  generateSingleProductIframe(payload: IGenerateSingleProductPackagePayload) {
    return this.api.post(`/packages/iframe`, payload)
  }

  generateMultipleProductPackage(
    payload: IGenerateMultipleProductPackagePayload
  ) {
    return this.api.post(`/packages`, payload)
  }

  copyProducts(payload: ICopyProductsPayload) {
    return this.api.post(`/products/copy`, payload)
  }

  copySingleProduct(payload: ICopySingleProductPayload) {
    const { id, country, shouldCopyAssets } = payload

    return this.api.post(`/products/${id}/copy`, { country, shouldCopyAssets })
  }

  autoTranslateSingleProduct(payload: IAutoTranslateSingleProductPayload) {
    const {
      id,
      sourceCountry,
      targetCountry,
      openPreviewAfterTranslation,
      openToEditionAfterTranslation,
      translationProgressId,
      translateAssets,
      contextId
    } = payload

    return this.api.post(`/ai-translations/products/${id}/auto-translate`, {
      sourceCountry,
      targetCountry,
      openPreviewAfterTranslation,
      openToEditionAfterTranslation,
      translationProgressId,
      translateAssets,
      contextId
    })
  }

  acceptDraftProduct(id: string, userId: string) {
    return this.api.put(`/ai-translations/products/${id}/accept-translation`, {
      acceptedByUserId: userId
    })
  }

  batchTranslateProducts(payload: IBatchTranslateRequest) {
    return this.api.post('/ai-translations/products/multi-translate', payload)
  }

  getBatchTranslationProgress(batchId: string) {
    return this.api.get(`/ai-translations/progress/batch/${batchId}`)
  }

  setProductBlocked(id: string, isBlocked: boolean) {
    return this.api.patch(`/products/${id}/blocked`, { isBlocked })
  }

  assignProductAllowedEditor(id: string, email: string) {
    return this.api.patch(`/products/${id}/allowed-editor`, { email })
  }
}

export default new ProductsRepository(HttpClient)
